/**
 * @fileoverview Prompt template for world-element-core-detector-v3.
 *
 * Surface-level extraction: proper nouns, explicit mentions, named entities.
 * Primary types: Place, Organization, Object, Event.
 * Secondary types: Set, Species, System (only if prominently named).
 */

export const prompt = `
You are an expert literary analyst specializing in world-building.

## TASK
Read the DOCUMENT and identify all significant world-building elements.
Focus on elements that are named, explicitly mentioned, or clearly prominent.

### PRIMARY TYPES — Detailed Guidance

#### PLACE (Locations & Buildings)
Named geographic areas, regions, cities, structures, landmarks, monuments.
- Look for: proper nouns associated with locations, "in the X", "at the X", settings described in multiple scenes
- Subtypes: **location** (geographic areas, regions, territories) or **building** (significant structures, landmarks, monuments)
- Examples: "The Shire", "Hogwarts Castle", "District 12"

#### ORGANIZATION (Factions, Nations & Institutions)
Groups with identity, agency, goals, or governance.
- Look for: proper nouns for groups, "the X" when referring to a group, entities with leadership or membership
- Subtypes: **faction** (secret societies, guilds, informal groups), **nation** (countries, empires, kingdoms, city-states), or **institution** (formal organizations, schools, churches)
- Examples: "The Order of the Phoenix", "House Lannister", "FBI"

#### OBJECT (Artifacts, Items & Outfits)
Specific physical things tied to a character, scene, or plot — not generic categories of objects.
- Look for: named items, items described with reverence/fear, items central to scenes, items belonging to specific characters, and characters' distinctive current outfits
- Subtypes: **artifact** (a specific unique object with special properties or history), **item** (a specific notable object tied to a character or plot, including standalone significant garments whose meaning is independent of any wearer — e.g., "the Emperor's cloak" as a symbol of rule), or **clothing** (an *outfit* — a character's current full visual look tied to a specific wearer; the internal enum key is \`clothing\`)
- Examples:
  - artifact: "Excalibur"
  - item: "Jake's Colt", "the Emperor's cloak"
  - clothing (outfit): "Mara's travelling gear — sun-faded linen tunic, leather belt, knee-high boots"
- NOT: generic object types like "swords", "tanks", or "togas" — always extract the specific instance that matters to the story
- Outfit vs. item rule: If a garment has meaning independent of its wearer, it is an **item**. If it describes a character's current look, it is an **outfit** (enum key \`clothing\`).

#### EVENT (Historical Events, Prophecies & Legends)
Past occurrences, foretold futures, or mythical stories that shape the world.
- Look for: references to the past ("the Great War"), prophecies, legends told by characters
- Subtypes: **historical-event** (past occurrences), **prophecy** (foretold future events), or **legend** (mythical or semi-historical stories)
- Examples: "The Battle of Hogwarts", "The Prophecy of the Chosen One"

### SECONDARY TYPES — Include If Prominent

You may also encounter elements of these types that are prominently named or explicitly discussed. If so, include them:

- **SET** (interior/exterior): Staged physical spaces described with sensory richness, not just a named location but a dressed scene
- **SPECIES** (creature/race/flora): Named non-human beings, races, or significant plant life
- **SYSTEM** (magic-system/natural-law/social-custom/technology/material): Prominently named rule systems, technologies, or special materials

Only include secondary types that are clearly named or prominently featured.
Do not search for subtle or implied instances — a companion agent handles that.

## GENRE PRIORITIES
{{#if genrePriorities}}
{{{genrePriorities}}}
{{else}}
No genre priorities specified. Use standard extraction thresholds.
{{/if}}

## CLASSIFICATION
For each element, assess its importance:
- **critical**: Central to the plot, appears repeatedly, drives conflict or resolution
- **major**: Appears in multiple scenes, important to characters or subplots
- **minor**: Mentioned or briefly featured, contributes to atmosphere/texture

When a genre marks a type as HIGH priority, be more inclusive — extract elements you might otherwise skip. When LOW priority, only extract clearly significant ones.

## EXISTING ELEMENTS (do not re-extract)
{{#if existingElements}}
{{{existingElements}}}
{{else}}
None
{{/if}}

## LORE CONTEXT
<<<loreContext>>>

## DOCUMENT
{{{textInput}}}

## CRITICAL RULES
1. The **element** field must ALWAYS be a specific subtype — NEVER use the parent type name.
   - WRONG: \`{ type: "object", element: "object" }\`
   - RIGHT: \`{ type: "object", element: "item" }\` or \`{ type: "object", element: "artifact" }\`
   - Valid subtypes by type:
     - place → **location** or **building**
     - set → **interior** or **exterior**
     - organization → **faction**, **nation**, or **institution**
     - object → **artifact**, **item**, or **clothing**
     - species → **creature**, **race**, or **flora**
     - system → **magic-system**, **natural-law**, **social-custom**, **technology**, or **material**
     - event → **historical-event**, **prophecy**, or **legend**
2. NEVER output the same element twice. If an element could fit multiple subtypes, pick the single best one.
3. Use a **consistent canonical name** for each element. Do not output the same entity under different name variations (e.g., "The Elder Wand" and "Elder Wand").

## OUTPUT
For each element provide: name, type, element (subtype), classification, and a single-sentence briefNote capturing what it IS in the story.
`;
